import {
  Button,
  Card,
  CrudMode,
  DataDisplay,
  Dialog,
  Grid,
  GridBreak,
  RichTextEditor,
  RichTextReader,
  Stack,
  Switch,
  Typography,
  ValidationErrors,
  pn,
} from "@compass/core";
import { FilePanel, Input, Select } from "components";
import { CopyLinkButton } from "components/common/CopyLinkButton";
import {
  Application,
  ApplicationAnswer,
  ApplicationEditResource,
  ApplicationQuestion,
  ApplicationSection,
  ApplicationSubQuestion,
  ApplicationViewResource,
  UploadFile,
} from "generated/openApi";
import produce from "immer";
import { useState } from "react";
import ApiService from "services/apiService";
import { initDataModel } from "utils";
import { createEntity } from "utils/CreateEntity";
import { ApplicationQrCodes } from "./ApplicationQrCodes";

type ApplicationGeneralInfoProps = {
  application: Application;
  setApplication: (value: Application) => void;
  setCopyApplication: (value: React.SetStateAction<number | undefined>) => void;
  canSwapGuid: boolean;
  onGuidSwapped: () => void;
  viewResource: ApplicationViewResource;
  editResource: ApplicationEditResource;
  crudMode: CrudMode;
  validationErrors: ValidationErrors;
};

export const ApplicationGeneralInfo = ({
  application,
  setApplication,
  setCopyApplication,
  canSwapGuid,
  onGuidSwapped,
  viewResource,
  editResource,
  crudMode,
  validationErrors,
}: ApplicationGeneralInfoProps) => {
  const [linkCopy, setLinkCopy] = useState<boolean>(false);
  const [copyExistingApplicationId, setCopyExistingApplicationId] = useState<number>();
  const [swapApplicationId, setSwapApplicationId] = useState<number>();
  const [swapDialogOpen, setSwapDialogOpen] = useState<boolean>(false);

  const swapApplication = async () => {
    if (!swapApplicationId) return;

    await ApiService.applications.swapGuid(application.id, swapApplicationId);
    setSwapDialogOpen(false);
    setSwapApplicationId(undefined);
    onGuidSwapped();
  };

  const copyExisting = (applicationId: number) => {
    const ignoredApplicationFields = [
      "createdById",
      "createdDate",
      "deactivatedDate",
      "language",
      "fileId",
      "id",
      "guid",
      "modifiedById",
      "modifiedDate",
      "centerLogoUploadFile",
      "leftLogoUploadFile",
      "rightLogoUploadFile",
      "qrCodeUploadFileId",
      "qrCodeUploadFile"
    ];
    const ignoredSectionFields = [
      "applicationId",
      "applicationQuestions",
      "fileId",
      "createdById",
      "createdDate",
      "id",
      "modifiedById",
      "modifiedDate",
    ];
    const ignoredQuestionFields = [
      "fileId",
      "createdById",
      "createdDate",
      "id",
      "modifiedById",
      "modifiedDate",
      "customEntityField",
      "applicationProperty",
      "formQuestionType",
      "formQuestionWriteInType",
      "applicationAnswers",
      "applicationSectionId",
      "applicationSubQuestions",
      "branchedFromFormAnswerIds",
      "branchToFormQuestionId",
      "branchedFromFormAnswers",
      "branchedFromFormQuestionIds",
      "branchedFromFormQuestions",
      "touched",
    ];
    const ignoredQuestionAnswerFields = [
      "createdById",
      "createdDate",
      "id",
      "modifiedById",
      "modifiedDate",
      "fileId",
      "applicationQuestionId",
      "branchToFormQuestionId",
      "branchToFormQuestionLabel",
      "otherWriteInType",
      "touched",
    ];
    const ignoredSubQuestionFields = [
      "applicationQuestionId",
      "createdById",
      "createdDate",
      "id",
      "modifiedById",
      "modifiedDate",
      "fileId",
      "touched",
    ];

    ApiService.applications.get(applicationId).then((copiedApp) => {
      let tempApplication: Application = createEntity<Application>({});
      for (var field in copiedApp) {
        if (!ignoredApplicationFields.includes(field)) tempApplication[field] = copiedApp[field];
      }

      const sectionsCopy: ApplicationSection[] = [];

      // copy sections
      copiedApp.applicationSections?.map((copiedSection) => {
        let tempSection = createEntity<ApplicationSection>({ applicationQuestions: [], applicationSubSections: [] });
        for (var field in copiedSection) {
          if (!ignoredSectionFields.includes(field)) tempSection[field] = copiedSection[field];
        }

        // copy questions
        copiedSection.applicationQuestions?.map((copiedQuestion) => {
          let tempQuestion = createEntity<ApplicationQuestion>({ applicationAnswers: [], applicationSubQuestions: [] });
          for (var field in copiedQuestion) {
            if (!ignoredQuestionFields.includes(field)) tempQuestion[field] = copiedQuestion[field];
          }

          // copy answers
          copiedQuestion.applicationAnswers?.map((copiedAnswer) => {
            let tempAnswer = createEntity<ApplicationAnswer>({});
            for (var field in copiedAnswer) {
              if (!ignoredQuestionAnswerFields.includes(field)) tempAnswer[field] = copiedAnswer[field];
            }
            tempQuestion.applicationAnswers?.push(tempAnswer);
          });

          // copy sub-questions
          copiedQuestion.applicationSubQuestions?.map((copiedSubQuestion) => {
            let tempSubQuestion = createEntity<ApplicationSubQuestion>({});
            for (var field in copiedSubQuestion) {
              if (!ignoredSubQuestionFields.includes(field)) tempSubQuestion[field] = copiedSubQuestion[field];
            }
            tempQuestion.applicationSubQuestions?.push(tempSubQuestion);
          });

          tempSection.applicationQuestions!.push(tempQuestion);
        });

        sectionsCopy.push(tempSection);
      });
      tempApplication.applicationSections = sectionsCopy;

      setApplication(tempApplication);
      setCopyApplication(applicationId);
    });
  };

  return (
    <>
    <Stack spacing="lg">
    <Card header={"Info"}>
      <Grid container spacing="sm">
        {application?.id != 0 && (
          <Grid item xs={12}>
            <Grid direction="row" container spacing="lg">
              <Grid item xs={11}>
                {application?.id != 0 && (
                  <DataDisplay label="Public Link">{`${viewResource?.publicUrl}${application?.guid}/${viewResource?.publicTenant}`}</DataDisplay>
                )}
              </Grid>
              <Grid item xs={1}>
                <CopyLinkButton
                  copyLink={`${viewResource?.publicUrl}${application?.guid}/${viewResource?.publicTenant}`}
                  showCheck={linkCopy}
                  setShowCheck={setLinkCopy}
                />
              </Grid>
            </Grid>
          </Grid>
        )}

        {canSwapGuid && application?.id != 0 && (
          <>
            <GridBreak />
            <Grid item md={6} xs={12}>
              <Select
                label="Swap Public Link With"
                value={viewResource?.existingApplications?.find((x) => x.id === swapApplicationId)}
                options={viewResource?.existingApplications?.filter((x) => x.id !== application.id)}
                getOptionLabel={(option) => option.name}
                onChange={(e, value) => setSwapApplicationId(value?.id)}
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <Button
                variant="contained"
                disabled={!swapApplicationId}
                onClick={() => setSwapDialogOpen(true)}
              >
                Swap Public Links
              </Button>
            </Grid>
          </>
        )}

        {crudMode === "edit" && application?.id == 0 && (
          <Grid item xs={6}>
            <Select
              label="Copy From Existing"
              value={viewResource?.existingApplications?.find((x) => x.id === copyExistingApplicationId)}
              options={viewResource?.existingApplications}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) => {
                setCopyExistingApplicationId(value?.id);
                copyExisting(value?.id);
              }}
            />
          </Grid>
        )}

        <GridBreak />

        <Grid item lg={3} md={4} xs={12}>
          {crudMode === "view" && <DataDisplay label="Name">{application?.name}</DataDisplay>}
          {crudMode === "edit" && (
            <Input
              label="Name"
              errors={validationErrors?.[pn<Application>("name")]}
              value={application?.name}
              required
              onChange={(e) =>
                setApplication(
                  produce(application, (draft) => {
                    draft!.name = e.target.value;
                  })
                )
              }
            />
          )}
        </Grid>

        <Grid item lg={3} md={4} xs={12}>
          {crudMode === "view" && <DataDisplay label="Title">{application?.title}</DataDisplay>}
          {crudMode === "edit" && (
            <Input
              label="Title"
              errors={validationErrors?.[pn<Application>("title")]}
              required
              value={application?.title}
              onChange={(e) =>
                setApplication(
                  produce(application, (draft) => {
                    draft!.title = e.target.value;
                  })
                )
              }
            />
          )}
        </Grid>

        <Grid item lg={3} md={4} xs={12}>
          {crudMode === "view" && <DataDisplay label="Sub-Title">{application?.subTitle}</DataDisplay>}
          {crudMode === "edit" && (
            <Input
              label="Sub-Title"
              errors={validationErrors?.[pn<Application>("subTitle")]}
              value={application?.subTitle}
              onChange={(e) =>
                setApplication(
                  produce(application, (draft) => {
                    draft!.subTitle = e.target.value;
                  })
                )
              }
            />
          )}
        </Grid>

        <Grid item lg={3} md={4} xs={12}>
          {crudMode === "view" && <DataDisplay label="Language">{application?.language?.name}</DataDisplay>}
          {crudMode === "edit" && (
            <Select
              label="Language"
              required
              errors={validationErrors?.[pn<Application>("languageId")]}
              value={editResource?.languages?.find((x) => x.id === application?.languageId)}
              options={editResource?.languages}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) =>
                setApplication(
                  produce(application, (draft) => {
                    draft!.languageId = value?.id;
                  })
                )
              }
            />
          )}
        </Grid>

        <Grid item lg={3} md={4} xs={12}>
          {crudMode === "view" && (
            <DataDisplay label="Parent Application">{application?.parentApplication}</DataDisplay>
          )}
          {crudMode === "edit" && (
            <Select
              label="Parent Application"
              errors={validationErrors?.[pn<Application>("parentApplicationId")]}
              value={editResource?.parentApplications?.find((x) => x.id === application?.parentApplicationId)}
              options={editResource?.parentApplications}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) =>
                setApplication(
                  produce(application, (draft) => {
                    draft!.parentApplicationId = value?.id;
                  })
                )
              }
            />
          )}
        </Grid>

        <Grid item xs={3}>
          {crudMode === "view" && (
            <DataDisplay label="Color Theme">{application.themeColor}</DataDisplay>
          )}
          {crudMode === "edit" && (
            <Input
            label="Color Theme"
            errors={validationErrors?.[pn<Application>("themeColor")]}
            value={application?.themeColor}
            onChange={(e) =>
              setApplication(
                produce(application, (draft) => {
                  draft!.themeColor = e.target.value;
                })
              )
            }
          />
          )}
        </Grid>

        <GridBreak />

        <Grid item xs={12}>
          {crudMode === "view" && <RichTextReader label="Submission Message" html={application?.submitMessage} />}
          {crudMode === "edit" && (
            <RichTextEditor
              label="Submission Message"
              hasColor
              hasAlign
              value={application?.submitMessage}
              onChange={(value) =>
                setApplication(
                  produce(application, (draft) => {
                    draft!.submitMessage = value!;
                  })
                )
              }
            />
          )}
        </Grid>

        <GridBreak />

        <Grid item md={4} xs={12}>
          <FilePanel
            label="Left Logo"
            fileName={application.leftLogoUploadFile?.fileName}
            fileRef={application?.leftLogoUploadFile?.fileRef}
            fileSize={application?.leftLogoUploadFile?.fileSize}
            uploadingData={application?.leftLogoUploadFile?.uploadingData}
            imagePreviewData={application?.leftLogoUploadFile?.imagePreviewData}
            onFileChange={(fileName, fileSize, uploadingData) =>
              setApplication(
                produce(application, (draft) => {
                  draft!.leftLogoUploadFile = initDataModel<UploadFile>({
                    fileName: fileName,
                    fileSize: fileSize,
                    uploadingData: uploadingData,
                  });
                })
              )
            }
            cancel={() =>
              setApplication(
                produce(application, (draft) => {
                  draft!.leftLogoUploadFile = undefined;
                  draft!.leftLogoUploadFileId = undefined;
                })
              )
            }
          />
        </Grid>

        <Grid item md={4} xs={12}>
          <FilePanel
            label="Center Logo"
            fileName={application?.centerLogoUploadFile?.fileName}
            fileRef={application?.centerLogoUploadFile?.fileRef}
            fileSize={application?.centerLogoUploadFile?.fileSize}
            uploadingData={application?.centerLogoUploadFile?.uploadingData}
            imagePreviewData={application?.centerLogoUploadFile?.imagePreviewData}
            onFileChange={(fileName, fileSize, uploadingData) =>
              setApplication(
                produce(application, (draft) => {
                  draft!.centerLogoUploadFile = initDataModel<UploadFile>({
                    fileName: fileName,
                    fileSize: fileSize,
                    uploadingData: uploadingData,
                  });
                })
              )
            }
            cancel={() =>
              setApplication(
                produce(application, (draft) => {
                  draft!.centerLogoUploadFile = undefined;
                  draft!.centerLogoUploadFileId = undefined;
                })
              )
            }
          />
        </Grid>

        <Grid item md={4} xs={12}>
          <FilePanel
            label="Right Logo"
            fileName={application?.rightLogoUploadFile?.fileName}
            fileRef={application?.rightLogoUploadFile?.fileRef}
            fileSize={application?.rightLogoUploadFile?.fileSize}
            uploadingData={application?.rightLogoUploadFile?.uploadingData}
            imagePreviewData={application?.rightLogoUploadFile?.imagePreviewData}
            onFileChange={(fileName, fileSize, uploadingData) =>
              setApplication(
                produce(application, (draft) => {
                  draft!.rightLogoUploadFile = initDataModel<UploadFile>({
                    fileName: fileName,
                    fileSize: fileSize,
                    uploadingData: uploadingData,
                  });
                })
              )
            }
            cancel={() =>
              setApplication(
                produce(application, (draft) => {
                  draft!.rightLogoUploadFile = undefined;
                  draft!.rightLogoUploadFileId = undefined;
                })
              )
            }
          />
        </Grid>

        <GridBreak />

        <Grid item xs={3}>
          {crudMode === "view" && (
            <DataDisplay label="Hide Signatures">{application?.optionalSignatures ? 'True' : 'False'}</DataDisplay>
          )}
          {crudMode === "edit" && (
            <Switch
              label="Hide Signatures"
              onClick={() => {
                setApplication(
                  produce(application, (draft) => {
                    draft!.optionalSignatures = !application?.optionalSignatures;
                  })
                );
              }}
              checked={application?.optionalSignatures}
            ></Switch>
          )}
        </Grid>
        
      </Grid>
    </Card>

    {application && application.id > 0 && (
      <ApplicationQrCodes application={application} setApplication={setApplication} />
    )}
    </Stack>
      <Dialog
        open={swapDialogOpen}
        maxWidth="sm"
        onClose={() => setSwapDialogOpen(false)}
        header={<Typography variant="inherit">Swap Public Links</Typography>}
        content={
          <Typography>
            Swap the public links for {application.name} and{" "}
            {viewResource?.existingApplications?.find((x) => x.id === swapApplicationId)?.name}? Existing shared links
            will open the opposite application. Saved QR codes will move with their links.
          </Typography>
        }
        footer={
          <Stack direction="row" spacing="sm">
            <Button onClick={() => setSwapDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={swapApplication}>Swap Public Links</Button>
          </Stack>
        }
      />
    </>
  );
};
